import {
  Color,
  InstancedBufferAttribute,
  InstancedMesh,
  MeshStandardMaterial,
  Object3D,
  Quaternion,
  Vector3,
} from 'three';
import { Layer } from './Layer.js';
import { createShellLibrary } from '../geometry/shell.js';
import { nacreTexture } from '../geometry/textures.js';

const UP = new Vector3(0, 1, 0);

/**
 * "…spiral staircases encrusted with spiral seashells."
 *
 * Six coiling regimes are grown once by `geometry/shell.js`, then scattered
 * in their thousands over the surfaces a walker can actually see: the fascia
 * across the well, the balustrade, the shaft wall, the edges of the treads
 * and the soffits overhead.
 *
 * Every shell is a candidate for desire. Attention makes nacre fire; enough
 * attention turns the shell into a memory, after which it keeps a low,
 * permanent glow and will not flare again.
 */
export class Seashells extends Layer {
  static id = 'seashells';
  static order = 40;

  async build() {
    const { settings, palette, helix, bus } = this.ctx;
    const cfg = settings.shells;
    this.cfg = cfg;

    this.library = createShellLibrary();
    this.material = this._material(palette);

    const placements = this._scatter(helix, settings, cfg);
    // Sorted by where they are on the descent, so a chunk is a short stretch
    // of stair and can be culled as one.
    placements.sort((a, b) => a.t - b.t);
    this.count = placements.length;

    // Flat arrays, shared with the gaze so it can test thousands of shells
    // against the attention ray without touching the GPU.
    this.positions = new Float32Array(this.count * 3);
    this.radii = new Float32Array(this.count);
    this.meshOf = new Uint16Array(this.count);
    this.localOf = new Uint32Array(this.count);
    this.glowOf = new Float32Array(this.count);
    this.rememberedOf = new Uint8Array(this.count);
    this.speciesOf = new Uint8Array(this.count);

    this._buildMeshes(placements);

    bus.on('remember', (e) => {
      if (e.kind !== 'shell') return;
      const i = e.data?.index ?? -1;
      if (i < 0) return;
      this.rememberedOf[i] = 1;
      this.ctx.audio?.shell({
        pan: e.data.pan ?? 0,
        bright: e.data.bright ?? 0.5,
        gain: 0.7,
      });
    });

    this.ctx.gaze.register({
      kind: 'shell',
      mode: 'cloud',
      maxDistance: 32,
      tolerance: 0.028,
      cloud: () => ({ positions: this.positions, radii: this.radii, count: this.count }),
      resolve: (index) => ({
        index,
        species: this.library[this.speciesOf[index]].name,
        bright: this.radii[index] < 0.12 ? 0.85 : 0.35,
        pan: 0,
      }),
    });

    this._tmpColor = new Color();
  }

  /**
   * Nacre: a thin-film interference term driven by the viewing angle, laid
   * over ordinary physically-based stone. Injected into MeshStandardMaterial
   * rather than written from scratch so the shells still take the scene's
   * light, the scene's fog and the scene's tone mapping.
   */
  _material(palette) {
    const grain = nacreTexture(256);
    grain.repeat.set(1, 1);

    const mat = new MeshStandardMaterial({
      color: palette.get('nacre').clone(),
      roughness: 0.32,
      metalness: 0.18,
      bumpMap: grain,
      bumpScale: 0.035,
      roughnessMap: grain,
      emissive: new Color(0x000000),
    });

    mat.userData.uniforms = {
      uIrid: { value: 0.85 },
      uGlowColor: { value: palette.get('nacreGlow').clone() },
      uTime: { value: 0 },
    };

    mat.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, mat.userData.uniforms);

      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          /* glsl */ `
          #include <common>
          attribute float aSeed;
          attribute float aGlow;
          varying float vSeed;
          varying float vGlow;
          varying vec2  vShellUv;
          `,
        )
        .replace(
          '#include <begin_vertex>',
          /* glsl */ `
          #include <begin_vertex>
          vSeed = aSeed;
          vGlow = aGlow;
          // three only declares vUv for some map combinations, so the shell
          // carries its own copy of the growth coordinates.
          vShellUv = uv;
          `,
        );

      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          /* glsl */ `
          #include <common>
          uniform float uIrid;
          uniform float uTime;
          uniform vec3  uGlowColor;
          varying float vSeed;
          varying float vGlow;
          varying vec2  vShellUv;
          `,
        )
        .replace(
          '#include <emissivemap_fragment>',
          /* glsl */ `
          #include <emissivemap_fragment>
          {
            vec3 V = normalize( vViewPosition );
            float fres = 1.0 - clamp( dot( normalize( normal ), V ), 0.0, 1.0 );

            // Shells are not all the same shell. A per-instance pigment:
            // bone, sand, rose, a few almost black with age.
            vec3 pigment = mix(
              mix( vec3( 1.0, 0.98, 0.94 ), vec3( 0.84, 0.72, 0.56 ), fract( vSeed * 3.1 ) ),
              vec3( 0.70, 0.50, 0.50 ),
              pow( fract( vSeed * 7.3 ), 3.4 )
            );
            pigment *= 1.0 - 0.45 * pow( fract( vSeed * 13.7 ), 3.0 );
            // Growth banding across the whorls.
            pigment *= 0.88 + 0.12 * sin( vShellUv.x * 46.0 + vSeed * 20.0 );
            diffuseColor.rgb *= pigment;

            // Thin-film: the optical thickness varies over the shell and with
            // the angle, so the colour runs the way it does on real nacre —
            // strongest at the grazing edge, almost absent face on.
            float phase = pow( fres, 1.6 ) * 2.35
                        + vSeed * 6.2831853
                        + vShellUv.x * 1.15
                        + vShellUv.y * 0.35;
            vec3 irid = 0.5 + 0.5 * cos( 6.2831853 * ( vec3( 0.0, 0.31, 0.62 ) + phase ) );
            float strength = uIrid * pow( fres, 2.1 ) * 0.55;
            diffuseColor.rgb = mix( diffuseColor.rgb, diffuseColor.rgb * ( 0.5 + irid ), strength );

            // Desire burning, then banked down into memory.
            float pulse = 0.78 + 0.22 * sin( uTime * 2.4 + vSeed * 31.0 );
            totalEmissiveRadiance += uGlowColor * irid * vGlow * vGlow * 2.6 * pulse;
            totalEmissiveRadiance += uGlowColor * vGlow * 0.5;
          }
          `,
        );
    };
    // Force a fresh program when the injection changes during development.
    mat.customProgramCacheKey = () => 'isidora-nacre-v2';
    return mat;
  }

  /**
   * Decide where every shell lives, before anything is allocated on the GPU.
   *
   * Shells are laid down in colonies: a seed position on the stair, then a
   * handful of shells crowding it, each a little smaller than the first. A
   * uniform scatter reads as confetti; this reads as something that grew.
   */
  _scatter(helix, settings, cfg) {
    const s = settings.stair;
    const zones = cfg.zones;
    const names = Object.keys(zones);
    const weightSum = names.reduce((a, n) => a + zones[n], 0);
    const rand = mulberry32(0x15ad0a);

    const apothem = s.wallRadius * Math.cos(Math.PI / settings.shaft.bays);
    const railRadius = s.innerRadius + 0.22;
    const stepArc = (Math.PI * 2) / s.stepsPerTurn;

    const out = [];
    const normal = new Vector3();
    const position = new Vector3();

    for (const name of names) {
      const want = Math.round((zones[name] / weightSum) * cfg.count);
      const colonies = Math.max(1, Math.round(want / cfg.clusterSize));

      for (let c = 0; c < colonies; c++) {
        const tSeed = 0.004 + rand() * 0.992;
        const members = Math.max(1, Math.round(cfg.clusterSize * (0.35 + rand() * 1.5)));
        // A colony has one good specimen and a crowd of smaller ones.
        const colonyScale = 0.45 + Math.pow(rand(), 1.6) * 1.1;

        for (let k = 0; k < members; k++) {
          const t = Math.min(
            0.998,
            Math.max(0.002, tSeed + (rand() - 0.5) * 2 * cfg.clusterSpread),
          );
          const a = helix.angleAt(t);
          const cos = Math.cos(a);
          const sin = Math.sin(a);
          const baseY = helix.heightAt(t);
          let scaleBias = colonyScale * (k === 0 ? 1.45 : 0.55 + rand() * 0.7);

          switch (name) {
            case 'fasciaIn': {
              // The face of the ribbon seen from across the well.
              const r = s.innerRadius - 0.36;
              const y = baseY - 0.08 - rand() * (s.fasciaDepth - 0.14);
              position.set(cos * r, y, sin * r);
              normal.set(-cos, (rand() - 0.5) * 0.4, -sin).normalize();
              scaleBias *= 1.25;
              break;
            }
            case 'fasciaOut': {
              // The face you walk past, at knee height, close enough to touch.
              const r = s.innerRadius + 0.07;
              const y = baseY - 0.1 - rand() * (s.fasciaDepth - 0.2);
              position.set(cos * r, y, sin * r);
              normal.set(cos, (rand() - 0.5) * 0.4, sin).normalize();
              scaleBias *= 1.1;
              break;
            }
            case 'balustrade': {
              const side = rand() < 0.42 ? -1 : 1;
              const r = railRadius + side * (0.07 + rand() * 0.06);
              // Crowding under the handrail, where a hand would not disturb them.
              const h =
                rand() < 0.55 ? s.railHeight * (0.74 + rand() * 0.26) : rand() * s.railHeight;
              position.set(cos * r, baseY + h, sin * r);
              normal.set(side * cos, 0.2 + rand() * 0.55, side * sin).normalize();
              scaleBias *= 0.62;
              break;
            }
            case 'wall': {
              const r = apothem - 0.28 - rand() * 0.3;
              const y = baseY + 0.55 + Math.pow(rand(), 1.4) * 3.6;
              position.set(cos * r, y, sin * r);
              normal.set(-cos, (rand() - 0.45) * 0.55, -sin).normalize();
              scaleBias *= 1.15;
              break;
            }
            case 'nosing': {
              // The front lip of a tread: the most-seen edge in the building,
              // and so the first thing to be colonised. They sit *on* the
              // stone, crowding the last few centimetres before the drop.
              const r = s.innerRadius + 0.25 + rand() * (s.outerRadius - s.innerRadius - 0.7);
              const edge = -stepArc * (0.40 + rand() * 0.08);
              const cosE = Math.cos(a + edge);
              const sinE = Math.sin(a + edge);
              // The tread's bevel lifts its top surface above the nominal
              // helix height; sitting at baseY would bury them in the stone.
              position.set(cosE * r, baseY + 0.042, sinE * r);
              // Tipped over the edge, the way a thing grows toward the drop.
              const tangentX = -sinE;
              const tangentZ = cosE;
              normal.set(tangentX * -0.42, 1, tangentZ * -0.42).normalize();
              scaleBias *= 0.5;
              break;
            }
            default: {
              // Soffit: the underside of the flight above, seen by looking up.
              const r = s.innerRadius + 0.25 + rand() * (s.outerRadius - s.innerRadius - 0.8);
              position.set(cos * r, baseY - s.treadThickness - 0.03, sin * r);
              normal.set((rand() - 0.5) * 0.5, -1, (rand() - 0.5) * 0.5).normalize();
              scaleBias *= 0.95;
              break;
            }
          }

          const scale = Math.max(
            cfg.minScale * 0.6,
            Math.min(
              cfg.maxScale,
              (cfg.minScale + Math.pow(rand(), 1.2) * (cfg.maxScale - cfg.minScale)) * scaleBias,
            ),
          );

          out.push({
            t,
            position: position.clone(),
            normal: normal.clone(),
            scale,
            spin: rand() * Math.PI * 2,
            seed: rand(),
            species: pickSpecies(this.library, rand()),
            zone: name,
          });
        }
      }
    }
    return out;
  }

  /**
   * One InstancedMesh per (stretch of stair × species).
   *
   * A single cloud of 5 200 shells cannot be culled — it is everywhere at
   * once — so the encrustation is cut into short runs of the descent. Each
   * run gets a real bounding sphere and ordinary frustum culling then does
   * its job: the GPU only ever sees the turns the traveller can reach.
   */
  _buildMeshes(placements) {
    const chunks = this.cfg.chunks;
    /** @type {Map<string, {species:number, chunk:number, list:Array}>} */
    const buckets = new Map();
    placements.forEach((p, i) => {
      const chunk = Math.min(chunks - 1, Math.floor(p.t * chunks));
      const key = `${chunk}:${p.species}`;
      let bucket = buckets.get(key);
      if (!bucket) {
        bucket = { species: p.species, chunk, list: [] };
        buckets.set(key, bucket);
      }
      bucket.list.push({ p, i });
    });

    this.meshes = [];
    this.glowAttrs = [];
    this.chunkY = [];

    const dummy = new Object3D();
    const quat = new Quaternion();
    const spinQuat = new Quaternion();

    for (const { species, chunk, list } of buckets.values()) {
      const meshIndex = this.meshes.length;
      const geo = this.library[species].geometry.clone();
      const mesh = new InstancedMesh(geo, this.material, list.length);
      mesh.castShadow = false;
      mesh.name = `shells-${chunk}-${this.library[species].name}`;

      const seeds = new Float32Array(list.length);
      const glows = new Float32Array(list.length);
      let minY = Infinity;
      let maxY = -Infinity;

      list.forEach(({ p, i }, local) => {
        quat.setFromUnitVectors(UP, p.normal);
        spinQuat.setFromAxisAngle(p.normal, p.spin);
        quat.premultiply(spinQuat);

        dummy.position.copy(p.position);
        dummy.quaternion.copy(quat);
        // Shells are not spheres: let them be a little long or a little fat.
        dummy.scale.set(p.scale * (0.86 + p.seed * 0.3), p.scale, p.scale * (0.9 + p.seed * 0.2));
        dummy.updateMatrix();
        mesh.setMatrixAt(local, dummy.matrix);

        seeds[local] = p.seed;
        glows[local] = 0;
        minY = Math.min(minY, p.position.y);
        maxY = Math.max(maxY, p.position.y);

        const i3 = i * 3;
        this.positions[i3] = p.position.x;
        this.positions[i3 + 1] = p.position.y + p.scale * 0.4;
        this.positions[i3 + 2] = p.position.z;
        this.radii[i] = p.scale * 0.55;
        this.speciesOf[i] = species;
        this.meshOf[i] = meshIndex;
        this.localOf[i] = local;
      });

      mesh.instanceMatrix.needsUpdate = true;
      geo.setAttribute('aSeed', new InstancedBufferAttribute(seeds, 1));
      const glowAttr = new InstancedBufferAttribute(glows, 1);
      glowAttr.setUsage(35048); // DynamicDrawUsage
      geo.setAttribute('aGlow', glowAttr);

      // The chunk wraps part of a turn, so its bounds are the whole shaft in
      // plan and only a few metres in section. Computing it by hand avoids
      // paying for `computeBoundingSphere` over every instance.
      mesh.boundingSphere = null;
      mesh.computeBoundingSphere();

      this.meshes.push(mesh);
      this.glowAttrs.push(glowAttr);
      this.chunkY.push((minY + maxY) * 0.5);
      this.group.add(mesh);
    }
  }

  update(dt, state) {
    const { palette, gaze, camera } = this.ctx;
    this.material.color.copy(palette.get('nacre'));
    const u = this.material.userData.uniforms;
    u.uGlowColor.value.copy(palette.get('nacreGlow'));
    u.uTime.value = state.elapsed;
    u.uIrid.value = 0.62 + 0.4 * (1 - state.t);

    const cam = camera.position;
    const gazedIndex =
      gaze.current && gaze.current.kind === 'shell' ? gaze.current.index : -1;
    const progress = gaze.progress;

    // Anything more than a few turns away is behind too much fog to matter.
    let visible = 0;
    for (let m = 0; m < this.meshes.length; m++) {
      const on = Math.abs(this.chunkY[m] - cam.y) < 64;
      this.meshes[m].visible = on;
      if (on) visible++;
    }
    this.visibleChunks = visible;

    const dirty = new Set();
    for (let i = 0; i < this.count; i++) {
      const i3 = i * 3;
      const dx = this.positions[i3] - cam.x;
      const dy = this.positions[i3 + 1] - cam.y;
      const dz = this.positions[i3 + 2] - cam.z;
      const d2 = dx * dx + dy * dy + dz * dz;

      let target = 0;
      // Anything close enough to touch keeps a faint private shimmer.
      if (d2 < 81) target = 0.07 * (1 - d2 / 81);
      if (this.rememberedOf[i]) target = Math.max(target, 0.3);
      if (i === gazedIndex) target = Math.max(target, 0.22 + progress * 0.95);

      const prev = this.glowOf[i];
      if (Math.abs(target - prev) < 0.0015 && target === 0 && prev === 0) continue;
      const next = prev + (target - prev) * Math.min(1, dt * 7.5);
      if (Math.abs(next - prev) < 0.0008) continue;
      this.glowOf[i] = next;
      const mesh = this.meshOf[i];
      this.glowAttrs[mesh].array[this.localOf[i]] = next;
      dirty.add(mesh);
    }
    for (const m of dirty) this.glowAttrs[m].needsUpdate = true;
  }

  stats() {
    return {
      shells: this.count,
      species: this.library.length,
      drawn: `${this.visibleChunks ?? 0}/${this.meshes.length}`,
      remembered: this.rememberedOf.reduce((a, b) => a + b, 0),
    };
  }
}

function pickSpecies(library, r) {
  const total = library.reduce((a, s) => a + s.weight, 0);
  let x = r * total;
  for (let i = 0; i < library.length; i++) {
    x -= library[i].weight;
    if (x <= 0) return i;
  }
  return library.length - 1;
}

/** A small deterministic PRNG, so the encrustation is the same every visit. */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
